import { apiClient } from './apiClient';

/**
 * What GET /v2/es/events serves: `report` (the periodic telemetry),
 * `dtmf` (the digits the gateway relays from the line), `calls` (the
 * telephony events: ring, dial, connect, hang up, network call) and
 * `sessions` (calls and DTMF together, regrouped per call by the backend).
 */
export type TelemetryEvent = 'report' | 'dtmf' | 'calls' | 'sessions';

/** The kinds the Monitoring panel offers: the telemetry, and the calls with their DTMF. */
export const TELEMETRY_EVENTS: TelemetryEvent[] = ['report', 'sessions'];

/** The telephony event codes `calls` covers (mqtt_ip_ta's EventCode). */
export type CallEventCode =
  | 'slic_ring' | 'slic_call' | 'slic_connected' | 'slic_dialed'
  | 'net_call' | 'line_connected' | 'line_dialed' | 'line_alerted' | 'hang_up';

interface TelemetryItemBase {
  /** ISO 8601, UTC. */
  at: string | null;
  /** The document's own event code (`report`, `dtmf`, `slic_call`…). */
  evt: string | null;
  evt_id: string | null;
}

/** One "report" the device sent: power, battery and radio state. */
export interface TelemetryReportItem extends TelemetryItemBase {
  pwr_lvl: number | null;
  battery: number | null;
  /** Modem CSQ, 0–31 as text. */
  radio_signal: string | null;
  volte: string | null;
  roaming: string | null;
  cell_id: string | null;
  mccmnc: string | null;
  next_msg: string | null;
}

/**
 * One DTMF the gateway relayed, e.g. `B;I0000000#` or `4;O04000400#`:
 * the key, then `I` (received from the line, i.e. from the alarm panel) or
 * `O` (sent by the gateway to the line) followed by duration and pause.
 */
export interface TelemetryDtmfItem extends TelemetryItemBase {
  digit: string | null;
  key: string | null;
  direction: 'I' | 'O' | null;
}

/** One telephony event; `number` is whichever of callerid / num / data the event carries. */
export interface TelemetryCallItem extends TelemetryItemBase {
  evt: CallEventCode | string | null;
  /** 1 = the line (handset / FXS), 2 = the network, as the gateway reports it. */
  origin: string | null;
  callerid: string | null;
  num: string | null;
  data: string | null;
  number: string | null;
}

/** A row of the `sessions` kind: a telephony event or a DTMF, with the fields of both. */
export type TelemetrySessionItem = TelemetryItemBase & Partial<Omit<TelemetryCallItem, keyof TelemetryItemBase>> & Partial<Omit<TelemetryDtmfItem, keyof TelemetryItemBase>>;

export type TelemetryItem = TelemetryReportItem | TelemetryDtmfItem | TelemetryCallItem | TelemetrySessionItem;

/** What a DTMF does: the dialing of the number, or its part in the P100 exchange (the alarm protocol the gateway relays). */
export type DtmfRole = 'dial' | 'invite' | 'ack' | 'retransmit' | 'voice' | 'end' | 'packet' | 'other';

export interface SessionDtmf {
  at: string;
  key: string | null;
  direction: 'I' | 'O' | null;
  digit: string | null;
  evt_id: string | null;
  role: DtmfRole;
}

/** A complete P100 data packet found among the received digits. */
export interface P100Packet {
  alarm_type: string;
  length: number;
  data: string;
  checksum: string;
  complete: boolean;
}

/**
 * One call as the backend regroups it: from the pick-up (or first telephony
 * event) to the hang-up, with its DTMF in the order they were exchanged.
 */
export interface TelemetrySession {
  start: string;
  end: string;
  /** The event that opened the session (`slic_call`, `net_call`, … or `dtmf` for digits outside a call). */
  opened_by: string | null;
  /** `hang_up`, `timeout` (ten minutes of silence) or `open` (still going at the end of the data). */
  closed_by: 'hang_up' | 'timeout' | 'open' | null;
  hang_up_origin?: string | null;
  origin: string | null;
  number: string | null;
  duration_s: number | null;
  dtmf_count: number;
  /** Every key of the call, in order, sent and received alike. */
  sequence: string;
  received_keys: string;
  p100: P100Packet | null;
  events: Array<{ at: string; evt: string | null; origin: string | null; number: string | null; evt_id: string | null }>;
  dtmf: SessionDtmf[];
}

export interface TelemetryResponse<T extends TelemetryItem = TelemetryItem> {
  device_id: string;
  evt: TelemetryEvent;
  /** The civil day queried, YYYY-MM-DD in `tz`. */
  date: string;
  tz: string;
  /** Newest first. */
  items: T[];
  /** Events on that day, which may exceed `items` when `truncated`. */
  total: number;
  truncated: boolean;
  /** Only for `sessions`: the calls of the day, newest first. */
  sessions?: TelemetrySession[];
}

export interface TelemetryQuery {
  evt: TelemetryEvent;
  /** YYYY-MM-DD; today when omitted. */
  date?: string;
  limit?: number;
}

/** Why a telemetry request failed, for the panel to word it. */
export type TelemetryFailure = 'unavailable' | 'forbidden' | 'failed';

export const classifyTelemetryError = (err: unknown): TelemetryFailure => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 503) return 'unavailable';
  if (status === 403 || status === 404) return 'forbidden';
  return 'failed';
};

class TelemetryService {
  async getDeviceEvents(deviceId: string, query: TelemetryQuery): Promise<TelemetryResponse> {
    const params = new URLSearchParams({ device_id: deviceId, evt: query.evt });
    if (query.date) params.set('date', query.date);
    if (query.limit) params.set('limit', String(query.limit));
    const response = await apiClient.get(`/v2/es/events?${params.toString()}`);
    return {
      ...response,
      items: Array.isArray(response?.items) ? response.items : [],
      sessions: Array.isArray(response?.sessions) ? response.sessions : undefined,
      total: Number(response?.total ?? 0),
      truncated: Boolean(response?.truncated),
    };
  }
}

export const telemetryService = new TelemetryService();
